import { Sample, geometryToLongitudeLatitude } from 'prove-common'
import Feature, { FeatureLike } from 'ol/Feature'
import Point from 'ol/geom/Point'
import { fromLonLat } from 'ol/proj'
import { Style, Text, Fill, Stroke, Circle as CircleStyle } from 'ol/style'
import OSM from 'ol/source/OSM'
import XYZ from 'ol/source/XYZ'
import { Coordinate } from 'ol/coordinate'
import { createEmpty, extend } from 'ol/extent'

export const toMapCoords = ([longitude, latitude]: [
  number,
  number
]): Coordinate => fromLonLat([longitude, latitude])

export const createFeaturesFromSamples = (
  samples: Sample[]
): Feature<Point>[] => {
  return samples
    .map((sample) => {
      if (sample.geometry?.coordinates) {
        const lonLat = geometryToLongitudeLatitude(sample.geometry)
        if (!lonLat) {
          return undefined
        }
        return new Feature({
          geometry: new Point(toMapCoords([lonLat.longitude, lonLat.latitude])),
          sampleId: sample.id,
          accuratePosition: sample.accuratePosition
        })
      }
      return undefined
    })
    .filter((f): f is Feature<Point> => f !== undefined)
}
const styleCache: Record<string, Style> = {}

export const getClusterStyle = (feature: FeatureLike, isDarkMode: boolean) => {
  const size = feature.get('features').length

  if (size === 1) {
    const originalFeature = feature.get('features')[0]
    const sampleId = originalFeature.get('sampleId')
    const accuratePosition = originalFeature.get('accuratePosition') !== false

    return getSampleStyle(sampleId, 20, isDarkMode, accuratePosition)
  }

  const cacheKey = `cluster-${size}-${isDarkMode ? 'dark' : 'light'}`
  if (styleCache[cacheKey]) {
    return styleCache[cacheKey]
  }

  const style = new Style({
    image: new CircleStyle({
      radius: 12,
      stroke: new Stroke({
        color: isDarkMode ? '#ffffff' : '#fff',
        width: 2
      }),
      fill: new Fill({
        color: isDarkMode ? '#054449' : '#3399CC'
      })
    }),
    text: new Text({
      text: size.toString(),
      fill: new Fill({
        color: '#fff'
      }),
      font: 'bold 12px Arial'
    })
  })

  styleCache[cacheKey] = style
  return style
}

export const getClusterExtent = (clusterFeature: FeatureLike) => {
  const features = clusterFeature.get('features')
  if (!features || features.length === 0) return null

  const extent = createEmpty()
  features.forEach((f: FeatureLike) => {
    const geometry = f.getGeometry()
    if (geometry) {
      extend(extent, geometry.getExtent())
    }
  })
  return extent
}

export const getSampleStyle = (
  sampleId: string,
  zoom: number | undefined,
  isDarkMode: boolean,
  accuratePosition = true
): Style => {
  const showText = zoom && zoom > 11.5

  return new Style({
    text: new Text({
      text: showText ? sampleId.toString() : '',
      font: 'arial',
      fill: new Fill({ color: isDarkMode ? '#FFFFFF' : 'black' }),
      stroke: new Stroke({ color: isDarkMode ? '#000000' : 'white', width: 2 }),
      offsetY: -10
    }),
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({
        color: accuratePosition ? '#E2F1DF' : 'rgba(226, 241, 223, 0.4)'
      }),
      stroke: new Stroke({
        color: accuratePosition ? '#054449' : 'rgba(5, 68, 73, 0.4)',
        width: 2
      })
    })
  })
}

export const getBaseLayerSource = (isDarkMode: boolean) => {
  if (isDarkMode) {
    return new XYZ({
      // Esri Dark Gray Canvas
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      attributions: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ',
      maxZoom: 16 // Note: Esri often stops zooming around level 16
    })
  }
  return new OSM()
}
